import { useCallback, useEffect, useRef, useState } from "react";
import { apiRequest } from "./api";

// Loads a JSON resource and exposes { data, loading, error, reload, setData }.
// A null path means "not applicable" and resolves to data = null without a request.
// Data is tied to the path it was loaded for: when the path changes (another cycle, another
// person) the previous result is hidden immediately instead of flashing until the new one arrives.
// A reload of the same path keeps the current data on screen.
export function useResource(path) {
    const [state, setState] = useState({ path, data: null, loading: Boolean(path), error: null });
    const mounted = useRef(true);
    const currentPath = useRef(path);
    currentPath.current = path;
    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);
    const load = useCallback(async () => {
        if (!path) { setState({ path, data: null, loading: false, error: null }); return; }
        setState(prev => (prev.path === path ? { ...prev, loading: true, error: null } : { path, data: null, loading: true, error: null }));
        try {
            const data = await apiRequest(path);
            if (mounted.current && currentPath.current === path) setState({ path, data, loading: false, error: null });
        } catch (error) {
            if (mounted.current && currentPath.current === path) setState(prev => ({ path, data: prev.path === path ? prev.data : null, loading: false, error }));
        }
    }, [path]);
    useEffect(() => { load(); }, [load]);
    const setData = useCallback(updater => setState(prev => ({ ...prev, data: typeof updater === "function" ? updater(prev.data) : updater })), []);
    const fresh = state.path === path;
    return {
        data: fresh ? state.data : null,
        loading: fresh ? state.loading : Boolean(path),
        error: fresh ? state.error : null,
        reload: load,
        setData,
    };
}

export const periodQuery = periodId => (periodId ? `?periodId=${encodeURIComponent(periodId)}` : "");
